import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { listReleases } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

/**
 * QoL Slice 5b — releasing history (per-cashier). Scoped cashiers only see
 * their own rows; admin / super_admin see all and can filter by cashier or
 * member from the admin SPA's member-detail "Releases" tab.
 */
export function ReleasingHistoryPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user?.roles.includes('admin') || !!user?.roles.includes('super_admin');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const q = useQuery({
    queryKey: ['release-history', from, to, user?.id],
    queryFn: () =>
      listReleases({
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        // Server already restricts non-admins to their own rows; passing the
        // cashier id explicitly when admin scopes to themselves keeps the
        // query semantics the same regardless of role.
        cashierId: isAdmin ? undefined : user?.id,
        limit: 100,
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Releasing history</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setFrom(''); setTo(''); }}
            disabled={!from && !to}
          >
            Clear
          </Button>
        </div>

        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !q.data?.length ? (
          <p className="text-sm text-muted-foreground">No releases match.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5">Order</th>
                <th className="px-2 py-1.5">Branch</th>
                {isAdmin && <th className="px-2 py-1.5">Cashier</th>}
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.orderId} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">
                    {new Date(r.releasedAt).toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{r.orderCode}</td>
                  <td className="px-2 py-1.5 text-xs">{r.branchCode ?? '—'}</td>
                  {isAdmin && (
                    <td className="px-2 py-1.5 text-xs">{r.cashierEmail ?? '—'}</td>
                  )}
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    ₱{Number(r.orderTotal).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
